// src/services/monitoring.js
//
// Phase 1: thin wrapper around Sentry so the rest of the app doesn't
// have to care whether monitoring is enabled.
//
// Sentry is loaded lazily — if REACT_APP_SENTRY_DSN is not set we
// export no-op functions so dev/test builds stay fast and offline.

let sentry = null;
let initialized = false;

/**
 * Initialize Sentry once, at app bootstrap.
 * Safe to call multiple times — subsequent calls are ignored.
 */
export async function initMonitoring() {
  if (initialized) return;
  initialized = true;

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // No DSN → stay silent; we're in dev or the user opted out.
    return;
  }

  try {
    // Dynamic import so Sentry isn't bundled when not configured.
    // eslint-disable-next-line import/no-unresolved
    const mod = await import('@sentry/react');
    sentry = mod;
    sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
      release: process.env.REACT_APP_VERSION,
      tracesSampleRate: Number(
        process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1,
      ),
      // Never ship PII — Quran data can contain student names.
      sendDefaultPii: false,
    });
  } catch (err) {
    // Sentry not installed or failed to init — degrade silently.
    // eslint-disable-next-line no-console
    console.warn('[monitoring] Sentry not available:', err?.message);
    sentry = null;
  }
}

export function captureException(error, context) {
  if (sentry) {
    try {
      sentry.captureException(error, context);
    } catch {
      /* ignore */
    }
  }
}

export function captureMessage(message, level = 'info') {
  if (sentry) {
    try {
      sentry.captureMessage(message, level);
    } catch {
      /* ignore */
    }
  }
}

export function setUser(user) {
  if (sentry) {
    try {
      sentry.setUser(
        user
          ? { id: user.uid, email: user.email /* no displayName to limit PII */ }
          : null,
      );
    } catch {
      /* ignore */
    }
  }
}
